import React from "react";
import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getValidToken } from "../../../utils/authUtils";
import { IncomeReportData } from "../types";
import { Loading } from "../../../components/Loading";
import IncomeSummary from "./IncomeSummary";
import DetailedIncomes from "./DetailedIncomes";
import { useReportFilter } from "../context/ReportFilterContext";

const IncomeReport: React.FC = () => {
  const { filterState } = useReportFilter();
  const { timeFilter, customStartDate, customEndDate, selectedVehicles } =
    filterState;

  const {
    data: incomeReportData,
    isLoading,
    isFetching,
    isError,
  } = useQuery<{
    data: IncomeReportData;
  }>({
    queryKey: [
      "incomeReport",
      timeFilter,
      customStartDate,
      customEndDate,
      selectedVehicles,
    ],
    queryFn: async () => {
      const token = await getValidToken();
      let url = `${process.env.REACT_APP_API_HOST}reports/income/${timeFilter}`;

      if (timeFilter === "custom") {
        url = `${process.env.REACT_APP_API_HOST}reports/income/custom`;
      }

      const params: any = {};
      if (timeFilter === "custom") {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      if (selectedVehicles.length > 0) {
        params.vehicleNumbers = selectedVehicles;
      }

      const response = await axios.get(url, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    enabled: timeFilter !== "custom" || (!!customStartDate && !!customEndDate),
  });

  const { summary } = incomeReportData?.data ?? {
    summary: { totalIncome: 0 },
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isLoading && (
        <Loading fullScreen={false} message="Loading income report..." />
      )}
      {isFetching && !isLoading && (
        <Loading fullScreen={false} message="Refreshing income report..." />
      )}

      {!isLoading && summary.totalIncome === 0 ? (
        <Typography
          textAlign="center"
          display="flex"
          justifyContent="center"
          alignItems="center"
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            px: 1.5,
            pb: 1,
            backgroundColor: "white",
            width: "100%",
            height: "100%",
            minHeight: "100px",
          }}
        >
          No income data available
        </Typography>
      ) : (
        !isLoading &&
        incomeReportData?.data && (
          <>
            <IncomeSummary data={incomeReportData.data} />
            <DetailedIncomes data={incomeReportData.data} />
          </>
        )
      )}
    </Box>
  );
};

export default IncomeReport;
